import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { AppFooter } from "@/components/AppFooter";
import {
  SquaresFour,
  MagnifyingGlass,
  FileText,
  Vault,
  SignOut,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/app", label: "Cruscotto", icon: SquaresFour, end: true, testid: "nav-dashboard" },
  { to: "/app/new", label: "Nuova indagine", icon: MagnifyingGlass, testid: "nav-new" },
  { to: "/app/reports", label: "Report", icon: FileText, testid: "nav-reports" },
  { to: "/app/ledger", label: "Registro di Audit", icon: Vault, testid: "nav-ledger" },
];

export function AppShell({ children, breadcrumb }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-zinc-50 text-left">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-zinc-200 bg-white flex flex-col">
        <div className="h-16 flex items-center px-5 border-b border-zinc-200">
          <BrandLogo variant="dark" showTagline />
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-zinc-950 text-white"
                    : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                }`
              }
            >
              <n.icon size={18} weight="duotone" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-zinc-200">
          <div className="px-2 py-2 mb-1">
            <div className="text-sm font-medium text-zinc-900 truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
            <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider border border-zinc-300 bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded-sm">
              {user?.tier === "investigator" ? "Investigator" : "Watcher"}
              {user?.role === "admin" && " · Operator"}
            </div>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm text-zinc-600 hover:bg-zinc-100 hover:text-red-600 transition-colors duration-150"
          >
            <SignOut size={18} weight="duotone" />
            Esci
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 border-b border-zinc-200 bg-white flex items-center px-6">
          <div className="text-sm text-zinc-500 font-mono" data-testid="breadcrumb">
            <span className="text-zinc-400">Certy Trace</span>
            {breadcrumb && <span className="mx-2 text-zinc-300">/</span>}
            {breadcrumb && <span className="text-zinc-900">{breadcrumb}</span>}
          </div>
          <div className="ml-auto flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            Legittimo Interesse · Art. 6.1.f
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          {children}
          <AppFooter className="mt-10 pt-5" />
        </main>
      </div>
    </div>
  );
}
